import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiQuery } from '@/hooks/use-api-query';
import { qk } from '@/lib/query/keys';

/** A row from GET /agent/credentials (the admin's own keys). */
export interface ApiKeyRow {
  publicId: string;
  name: string | null;
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  expiresAt: string | null;
  retrievable?: boolean;
}

export interface CreatedKey {
  token: string;
  publicId: string;
  name: string;
  scopes: string[];
}

/** The admin's own keys — retrievable + revocable. */
export function useMyApiKeys(orgId: string | undefined) {
  return useApiQuery<ApiKeyRow[]>(
    qk.org(orgId ?? '', 'api-keys', 'mine'),
    '/agent/credentials',
    { enabled: !!orgId },
  );
}

function useApiKeysInvalidate(orgId: string | undefined) {
  const qc = useQueryClient();
  return () =>
    orgId ? qc.invalidateQueries({ queryKey: qk.org(orgId, 'api-keys') }) : undefined;
}

export function useCreateApiKey(orgId: string | undefined) {
  const invalidate = useApiKeysInvalidate(orgId);
  return useApiMutation<CreatedKey, { name: string; expiresAt?: string }>(
    (vars) => ({
      path: '/agent/credentials',
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vars),
      },
      fallback: 'Failed to create the key',
    }),
    { onSuccess: invalidate },
  );
}

export function useRevokeApiKey(orgId: string | undefined) {
  const invalidate = useApiKeysInvalidate(orgId);
  return useApiMutation<void, { publicId: string }>(
    ({ publicId }) => ({
      path: `/agent/credentials/${publicId}`,
      init: { method: 'DELETE' },
      fallback: 'Failed to revoke the key',
    }),
    { onSuccess: invalidate },
  );
}
